import Pubnub from 'pubnub';
import { useContext, useStore } from '@nuxtjs/composition-api';
import { useLocalStorage } from '@vueuse/core';
import { computed, inject, provide, ref } from 'vue';
import { IProfileState } from '~/store/profile/types';
import {
  ActionMetadata,
  IChannel,
  IChat,
  IEnvelope,
  IMemberState,
  IMembershipCustom,
  IUserCustom,
  Timetoken,
} from '~/types/chat';

export const INJECTION_KEY = '$pubnubchat';

export const isFullUserData = (
  data: any,
): data is Pubnub.UUIDMetadataObject<IUserCustom> => {
  return (
    data.hasOwnProperty('custom') ||
    data.hasOwnProperty('name') ||
    data.hasOwnProperty('externalId') ||
    data.hasOwnProperty('profileUrl') ||
    data.hasOwnProperty('email')
  );
};

export const isValidTimetoken = (timetoken: string | number) => {
  return !!timetoken && !isNaN(+timetoken);
};

export const getNumericTimetoken = (timetoken: string | number) => {
  return isValidTimetoken(timetoken) ? BigInt(timetoken) : 0;
};

export const useChatProvider = (params: IChannel) => {
  const { $pubnub: pubnub } = useContext();
  const {
    commit,
    state: { profile },
  } = useStore<{ profile: IProfileState }>();
  const getPubnubUserId: IChat['getPubnubUserId'] = (userId) => {
    return `chatUser|${userId}`;
  };

  if (profile.user?.loggedIn) {
    pubnub.setUUID(getPubnubUserId(profile.user.id));
  }

  const members: IChat['members'] = ref({});
  const messages: IChat['messages'] = ref([]);
  const isFetchingMessages = ref(false);
  const isUserBanned = computed(() => {
    const member = members.value?.[pubnub.getUUID()];
    return member?.data?.uuid &&
      isFullUserData(member?.data?.uuid) &&
      !!member.data?.uuid?.custom?.ban
      ? true
      : false;
  });

  const lastReadTimetoken = useLocalStorage<Timetoken | undefined>(
    `galafilm:chat:lastReadTimetoken:${params.channel}`,
    '',
  );

  const setMemberState = async (uuid: string, state: IMemberState) => {
    members.value = {
      ...members.value,
      [uuid]: state,
    };
  };

  const setMemberData = async (
    data: Pubnub.UUIDMetadataObject<IUserCustom>,
  ) => {
    const { id, eTag, updated, ...rest } = data;
    const oldMemberData = members.value?.[id] ?? {};
    const newMemberData = {
      loading: false,
      data: {
        ...(oldMemberData.data || {}),
        eTag,
        updated,
        uuid: {
          ...(oldMemberData.data?.uuid || { id }),
          ...rest,
        },
      },
    };
    setMemberState(id, newMemberData);
  };

  const handleMessageAction = async (e: Pubnub.MessageActionEvent) => {
    if (e.channel !== params.channel) {
      return;
    }

    const messageIdx = messages.value.findIndex(
      (m) => m.timetoken === e.data.messageTimetoken,
    );

    if (messageIdx === -1 || !messages.value[messageIdx]) {
      return;
    }

    const clonedMesages = [...messages.value];
    const message = clonedMesages[messageIdx];
    const { type, value, actionTimetoken, uuid } = e.data;
    const actions = message?.actions?.[type]?.[value] || [];

    const updateActions = (newActions: ActionMetadata[]) => {
      message.actions = {
        ...message.actions,
        [type]: {
          ...message.actions?.[type],
          [value]: newActions,
        },
      };
    };

    if (e.event === 'added') {
      const newActions = [...actions, { uuid, actionTimetoken }];
      updateActions(newActions);
    }

    if (e.event === 'removed') {
      const newActions = actions.filter(
        (a) => a.actionTimetoken.toString() !== actionTimetoken,
      );
      if (newActions.length) {
        updateActions(newActions);
      } else {
        const { [value]: deletedValue, ...rest } =
          message.actions?.[type] || {};
        message.actions = {
          ...message.actions,
          [type]: rest,
        };
      }
    }

    messages.value = clonedMesages;
  };

  const handleMessage = async (e: Pubnub.MessageEvent) => {
    if (e.channel !== params.channel) {
      return;
    }

    if (!e.message.message && !e.message.hasOwnProperty('text')) {
      return;
    }

    messages.value.push({
      timetoken: e.timetoken,
      uuid: e.publisher,
      message: e.message,
    });
  };

  const handleObjects = async (
    e: Pubnub.ObjectsEvent<IUserCustom, Pubnub.ObjectCustom, IMembershipCustom>,
  ) => {
    if (
      e?.message?.event === 'set' &&
      e.message?.type === 'uuid' &&
      e.message?.data?.id
    ) {
      // if current user is a channel member, this event will be broadcast on both subscribed
      // channels and we only want to update once
      if (e?.channel === pubnub.getUUID()) {
        setMemberData(e.message.data);
        return;
      }
      // only update if member has already been fetched (i.e. by recently viewed message)
      if (members?.value?.[e.message?.data?.id]?.data?.uuid?.id) {
        setMemberData(e.message.data);
      }
    }
  };

  // const handleStatus = async (e: Pubnub.StatusEvent) => {
  //   console.log(e);
  // }

  const fetchMessages = async () => {
    try {
      isFetchingMessages.value = true;
      const fetchedMessages = await pubnub.fetchMessages({
        channels: [params.channel],
        // count: 100,
        count: 25, // max is 25 when fetching messages with actions
        includeMessageActions: true,
      });

      if (fetchedMessages.channels?.[params.channel]) {
        messages.value = fetchedMessages.channels[params.channel].map(
          (message) => {
            const data: IEnvelope = {
              timetoken: message.timetoken.toString(),
              uuid: message.uuid || '',
              message: message.message,
              actions: message.actions,
            };
            return data;
          },
        );
      }
      isFetchingMessages.value = false;
    } catch (e) {
      console.error('Error fetching messages', e);
    }
  };

  const setUserMetadata = async () => {
    try {
      const userData: Pubnub.UUIDMetadata<Pubnub.ObjectCustom> = {
        name: profile.user.displayName,
      };
      if (profile.user.profilePhotoUrl) {
        userData['profileUrl'] = profile.user.profilePhotoUrl;
      }
      await pubnub.objects.setUUIDMetadata({
        uuid: getPubnubUserId(profile.user.id),
        data: userData,
      });
    } catch (e) {
      console.error('Error setting UUID metadata', e);
    }
  };

  const addMember = async (uuid: string) => {
    if (
      members.value?.[uuid]?.loading ||
      members.value?.[uuid]?.data?.uuid?.id
    ) {
      return;
    }
    try {
      setMemberState(uuid, { loading: true });
      const { data } = await pubnub.objects.getUUIDMetadata<IUserCustom>({
        uuid,
        include: {
          customFields: true,
        },
      });
      setMemberData(data);
    } catch (e) {
      console.error('Error getting UUID metadata', e);
    }
  };

  const fetchCurrentUserData = async () => {
    const uuid = pubnub.getUUID();
    try {
      setMemberState(uuid, { loading: true });
      const { data } = await pubnub.objects.getChannelMembers<
        IMembershipCustom,
        IUserCustom
      >({
        channel: params.channel,
        include: {
          customFields: true,
          UUIDFields: true,
          customUUIDFields: true,
        },
        filter: `uuid.id == "${uuid}"`,
      });

      if (!data?.length) {
        setMemberData({
          id: uuid,
          eTag: '',
          updated: '',
        });
        return;
      }

      setMemberData(
        isFullUserData(data[0].uuid)
          ? data[0].uuid
          : {
              id: uuid,
              eTag: data[0].eTag,
              updated: data[0].updated,
            },
      );

      if (data[0].custom?.lastReadTimetoken) {
        lastReadTimetoken.value = data[0].custom.lastReadTimetoken;
      }
    } catch (e) {
      console.error('Error getting channel members', e);
    }
  };

  // const removeChannelMember = async () => {
  //   try {
  //     await pubnub.objects.removeChannelMembers({
  //       channel: params.channel,
  //       uuids: [pubnub.getUUID()],
  //     });
  //   } catch (e) {
  //     console.error('Error removing channel member', e);
  //   }
  // };

  const setChannelMember = async () => {
    try {
      await pubnub.objects.setChannelMembers({
        channel: params.channel,
        uuids: [pubnub.getUUID()],
      });
    } catch (e) {
      console.error('Error setting channel member', e);
    }
  };

  const setChannelMetadata = async () => {
    try {
      const channelData: Pubnub.ChannelMetadata<{ group: string }> = {
        name: params.title,
        description: params.description || '',
        custom: {
          group: params.group || '',
        },
      };
      await pubnub.objects.setChannelMetadata({
        channel: params.channel,
        data: channelData,
      });
    } catch (e) {
      console.error('Error setting channel metadata', e);
    }
  };

  const deleteMessage: IChat['deleteMessage'] = async (timetoken) => {
    try {
      pubnub.addMessageAction({
        channel: params.channel,
        messageTimetoken: timetoken,
        action: {
          type: 'deleted',
          value: '.',
        },
      });
    } catch (e) {
      console.error('Error deleting message', e);
    }
  };

  const reportMessage: IChat['reportMessage'] = async (timetoken) => {
    if (isUserBanned.value) {
      return;
    }

    try {
      await pubnub.addMessageAction({
        channel: params.channel,
        messageTimetoken: timetoken,
        action: {
          type: 'reported',
          value: pubnub.getUUID(),
        },
      });
      commit('updateSnackbarSuccessText', 'Message reported successfully');
      commit('toggleSuccessSnackbar', true);
    } catch (e) {
      console.error('Error reporting message', e);
    }
  };

  const addReaction: IChat['addReaction'] = async ({
    messageTimetoken,
    reaction,
  }) => {
    try {
      await pubnub.addMessageAction({
        channel: pubnub.getSubscribedChannels()[0],
        messageTimetoken,
        action: {
          type: 'reaction',
          value: reaction,
        },
      });
    } catch (e) {
      console.error('Error adding reaction', e);
    }
  };

  const removeReaction: IChat['removeReaction'] = async ({
    messageTimetoken,
    actionTimetoken,
  }) => {
    try {
      await pubnub.removeMessageAction({
        channel: pubnub.getSubscribedChannels()[0],
        messageTimetoken: messageTimetoken.toString(),
        actionTimetoken: actionTimetoken.toString(),
      });
    } catch (e) {
      console.error('Error adding reaction', e);
    }
  };

  const setLastReadTimetoken: IChat['setLastReadTimetoken'] = async (
    timetoken,
  ) => {
    if (
      getNumericTimetoken(timetoken) <
      getNumericTimetoken(lastReadTimetoken.value ?? '')
    ) {
      return;
    }
    try {
      await pubnub.objects.setMemberships<IMembershipCustom, IMembershipCustom>(
        {
          channels: [
            {
              id: params.channel,
              custom: {
                lastReadTimetoken: timetoken,
              },
            },
          ],
        },
      );
      lastReadTimetoken.value = timetoken;
    } catch (e) {
      console.error('Error setting last read timetoken', e);
    }
  };

  const init = async () => {
    const userId = pubnub.getUUID();
    pubnub.addListener({
      message: handleMessage,
      messageAction: handleMessageAction,
      objects: handleObjects,
      // status: handleStatus,
    });
    await Promise.all([
      setUserMetadata(),
      setChannelMetadata(),
      setChannelMember(),
    ]);
    pubnub.subscribe({ channels: [params.channel, userId] });
    // get user metadata + custom fields + last read timetoken
    fetchCurrentUserData();
    // get initial messages
    fetchMessages();
  };

  const destroy = async () => {
    pubnub.removeListener({
      message: handleMessage,
      messageAction: handleMessageAction,
      objects: handleObjects,
    });
    pubnub.unsubscribe({ channels: [params.channel, pubnub.getUUID()] });
    // Removing memeber removes membership data (i.e. last read timetoken)
    // await removeChannelMember();
  };

  provide<IChat>(INJECTION_KEY, {
    channel: params,
    isFetchingMessages,
    isUserBanned,
    lastReadTimetoken,
    members,
    messages,
    pubnub,
    init,
    destroy,
    addMember,
    addReaction,
    deleteMessage,
    fetchMessages,
    getPubnubUserId,
    removeReaction,
    reportMessage,
    setLastReadTimetoken,
  });
};

export const useChat = () => {
  const chat = inject<IChat>(INJECTION_KEY);
  if (!chat) {
    throw new Error('useChat must be used within a useChatProvider');
  }
  return chat;
};
